"use client";

import Image from "next/image";
import { useState } from "react";
import { useCarouselDrag } from "@/app/hooks/useCarouselDrag";

const depoimentos = [
  {
    id: 1,
    quote: `Trabalhar no Agibank é ver o crescimento em conjunto e o aprendizado contínuo acontecer na prática. Aqui, você é responsável pela sua carreira e nós te damos todas as condições para se destacar nessa jornada. Então, se você quer crescer com a gente, saiba que toda sua energia deve ser voltada para alcançar os seus resultados e, consequentemente, os da empresa.`,
    highlightStart: "Trabalhar no Agibank",
    highlight: "é ver o crescimento em conjunto e o aprendizado contínuo acontecer na prática",
    name: "Lucas Araujo de Aguiar",
    role: "Diretor de Gente e Governança",
    image: "/images/home/depoimento-01.png",
  },
  {
    id: 2,
    quote: `Trabalhar no Agibank há mais de 16 anos é desafiador. Não soltamos a mão de ninguém, e transformamos pressão em foco, desafio em aprendizado e meta em conquista. Nessa jornada, mesmo com mudanças e novos contextos, sigo firme na disciplina e no trabalho em equipe, porque aqui, um resultado consistente nasce de cultura forte, parceria e gente comprometida em fazer acontecer todos os dias.`,
    highlightStart: "Trabalhar no Agibank há mais de 16 anos",
    highlight: "é desafiador",
    name: "Caliani Medeiros",
    role: "Gerente Comercial",
    image: "/images/home/depoimento-02.png",
  },
  {
    id: 3,
    quote: `O dia a dia do time de tecnologia envolve o compromisso com o cliente e com o negócio, sempre. Isso porque tecnologia é meio, não é fim. 
    Mas é um meio altamente especializado e super crítico para o nosso negócio. Por isso, as pessoas que trabalham aqui precisam ter brilho no olho e devem se dedicar para que a tecnologia esteja a favor das pessoas.`,
    highlightStart: "O dia a dia do time de tecnologia envolve o compromisso com o cliente e com o negócio,",
    highlight: "sempre",
    name: "Vinicius Birkeland Aloe",
    role: "Diretor de Tecnologia",
    image: "/images/home/depoimento-03.png",
  },
  {
    id: 4,
    quote: `Como banco, não podemos tomar decisões sem dados. 
    E atuar aqui no Corporativo do Agibank, mais especificamente nessa área, eu consigo ter uma visão mais 360º do negócio. Trabalhar aqui significa tentar traduzir, sempre, a complexidade dos números ou dos nossos produtos, para a realidade dos clientes. E quando conseguimos isso, não tem preço!`,
    highlightStart: "Como banco,",
    highlight: "não podemos tomar decisões sem dados",
    name: "Guilherme Vivian",
    role: "Gerente de Dados",
    image: "/images/home/depoimento-04.png",
  },
];

export default function Depoimentos() {
  const [currentIndex, setCurrentIndex] = useState(0);

  const goToPrevious = () => {
    setCurrentIndex((prev) =>
      prev === 0 ? depoimentos.length - 1 : prev - 1
    );
  };

  const goToNext = () => {
    setCurrentIndex((prev) =>
      prev === depoimentos.length - 1 ? 0 : prev + 1
    );
  };

  const getSlideIndex = (offset: number) => {
    let index = currentIndex + offset;
    if (index < 0) index = depoimentos.length + index;
    if (index >= depoimentos.length) index = index - depoimentos.length;
    return index;
  };

  const dragProps = useCarouselDrag({
    onSwipeLeft: goToNext,
    onSwipeRight: goToPrevious,
  });

  const currentDepoimento = depoimentos[currentIndex];

  return (
    <section className="py-16 lg:py-24 bg-white overflow-hidden">
      <div className="max-w-[1200px] mx-auto px-4">
        {/* Title */}
        <h2 className="text-[36px] lg:text-[43px] font-bold text-center text-primary-main mb-12 lg:mb-16">
          Como é trabalhar no Agibank?
        </h2>

        {/* Carousel Container */}
        <div className="relative">
          {/* Cards Container */}
          <div className="relative flex items-center justify-center gap-8 lg:gap-12 select-none" {...dragProps}>
            {/* Previous Card (Left) */}
            <div className="hidden lg:block absolute left-0 w-[600px] h-[467px] opacity-60 z-0">
              <div className="bg-white rounded-[24px] shadow-lg overflow-hidden h-full flex">
                <div className="flex-1 p-8 flex flex-col">
                  {/* Quote icon */}
                  <div className="mb-4 text-blue-main">
                    <svg
                      width="24"
                      height="20"
                      viewBox="0 0 30 24"
                      fill="currentColor"
                    >
                      <path d="M0 24V14.4C0 10.08 0.9 6.6 2.7 3.96C4.5 1.32 7.5 0 11.7 0V6C9.1 6 7.3 6.66 6.3 7.98C5.3 9.3 4.8 11.04 4.8 13.2H11.7V24H0ZM18.3 24V14.4C18.3 10.08 19.2 6.6 21 3.96C22.8 1.32 25.8 0 30 0V6C27.4 6 25.6 6.66 24.6 7.98C23.6 9.3 23.1 11.04 23.1 13.2H30V24H18.3Z" />
                    </svg>
                  </div>
                  <p className="text-sm text-blue-dark line-clamp-6 flex-1">
                    {depoimentos[getSlideIndex(-1)].quote}
                  </p>
                  <div className="mt-4">
                    <p className="font-bold text-blue-dark text-sm">
                      {depoimentos[getSlideIndex(-1)].name}
                    </p>
                    <p className="text-xs text-blue-dark/70">
                      {depoimentos[getSlideIndex(-1)].role}
                    </p>
                  </div>
                </div>
                <div className="relative w-[200px] shrink-0">
                  <Image
                    src={depoimentos[getSlideIndex(-1)].image}
                    alt={depoimentos[getSlideIndex(-1)].name}
                    fill
                    className="object-cover object-top lg:object-center"
                  />
                </div>
              </div>
            </div>

            {/* Current Card (Center) */}
            <div className="relative z-10 w-full max-w-[924px] bg-white rounded-[30px] shadow-[0px_33px_196px_0px_rgba(0,0,0,0.07),0px_7px_44px_0px_rgba(0,0,0,0.04),0px_2px_13px_0px_rgba(0,0,0,0.03)] overflow-hidden">
              <div className="flex flex-col md:flex-row">
                {/* Content */}
                <div className="flex-1 p-8 lg:p-12 flex flex-col">
                  {/* Quote icon */}
                  <div className="mb-6 lg:mb-8 text-blue-main">
                    <svg
                      width="30"
                      height="24"
                      viewBox="0 0 30 24"
                      fill="currentColor"
                    >
                      <path d="M0 24V14.4C0 10.08 0.9 6.6 2.7 3.96C4.5 1.32 7.5 0 11.7 0V6C9.1 6 7.3 6.66 6.3 7.98C5.3 9.3 4.8 11.04 4.8 13.2H11.7V24H0ZM18.3 24V14.4C18.3 10.08 19.2 6.6 21 3.96C22.8 1.32 25.8 0 30 0V6C27.4 6 25.6 6.66 24.6 7.98C23.6 9.3 23.1 11.04 23.1 13.2H30V24H18.3Z" />
                    </svg>
                  </div>

                  {/* Quote text */}
                  <div className="text-lg lg:text-xl text-blue-dark mb-8 flex-1">
                    <p className="mb-4">
                      {currentDepoimento.highlightStart}{" "}
                      <strong className="font-bold">
                        {currentDepoimento.highlight}
                      </strong>
                      .
                    </p>
                    <p className="whitespace-pre-line">
                      {currentDepoimento.quote
                        .split(currentDepoimento.highlight + ".")[1]
                        ?.trim()}
                    </p>
                  </div>

                  {/* Author */}
                  <div>
                    <p className="font-bold text-blue-dark text-base">
                      {currentDepoimento.name}
                    </p>
                    <p className="text-sm text-blue-dark/70">
                      {currentDepoimento.role}
                    </p>
                  </div>
                </div>

                {/* Image */}
                <div className="relative w-full md:w-[280px] lg:w-[350px] h-[380px] md:h-auto md:min-h-[500px] lg:min-h-[580px] shrink-0">
                  <Image
                    src={currentDepoimento.image}
                    alt={currentDepoimento.name}
                    fill
                    className="object-cover object-top lg:object-center"
                  />
                </div>
              </div>
            </div>

            {/* Next Card (Right) */}
            <div className="hidden lg:block absolute right-0 w-[600px] h-[467px] opacity-60 z-0">
              <div className="bg-white rounded-[24px] shadow-lg overflow-hidden h-full flex">
                <div className="flex-1 p-8 flex flex-col">
                  {/* Quote icon */}
                  <div className="mb-4 text-blue-main">
                    <svg
                      width="24"
                      height="20"
                      viewBox="0 0 30 24"
                      fill="currentColor"
                    >
                      <path d="M0 24V14.4C0 10.08 0.9 6.6 2.7 3.96C4.5 1.32 7.5 0 11.7 0V6C9.1 6 7.3 6.66 6.3 7.98C5.3 9.3 4.8 11.04 4.8 13.2H11.7V24H0ZM18.3 24V14.4C18.3 10.08 19.2 6.6 21 3.96C22.8 1.32 25.8 0 30 0V6C27.4 6 25.6 6.66 24.6 7.98C23.6 9.3 23.1 11.04 23.1 13.2H30V24H18.3Z" />
                    </svg>
                  </div>
                  <p className="text-sm text-blue-dark leading-relaxed line-clamp-6 flex-1">
                    {depoimentos[getSlideIndex(1)].quote}
                  </p>
                  <div className="mt-4">
                    <p className="font-bold text-blue-dark text-sm">
                      {depoimentos[getSlideIndex(1)].name}
                    </p>
                    <p className="text-xs text-blue-dark/70">
                      {depoimentos[getSlideIndex(1)].role}
                    </p>
                  </div>
                </div>
                <div className="relative w-[200px] shrink-0">
                  <Image
                    src={depoimentos[getSlideIndex(1)].image}
                    alt={depoimentos[getSlideIndex(1)].name}
                    fill
                    className="object-cover"
                  />
                </div>
              </div>
            </div>

            {/* Navigation Arrows */}
            <button
              onClick={goToPrevious}
              className="absolute left-4 lg:left-8 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-primary-main/40 backdrop-blur-sm flex items-center justify-center text-white hover:bg-primary-main/60 transition-colors shadow-lg"
              aria-label="Anterior"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M8.69471 20.9758C9.11979 20.4898 9.11979 19.6829 8.70304 19.1871L3.63459 13.2667L22.9246 13.2667C23.5165 13.2667 24 12.7028 24 12.0029C24 11.3029 23.5165 10.7391 22.9246 10.7391L3.64292 10.7391L8.71137 4.81868C9.12812 4.32289 9.11979 3.52572 8.70304 3.02993C8.27795 2.54386 7.60259 2.54386 7.17751 3.03965L0.308378 11.1085C0.216666 11.2252 0.141666 11.3515 0.0833325 11.5071C0.0249992 11.6626 0 11.8279 0 11.9932C0 12.3237 0.108333 12.6348 0.308378 12.8778L7.17751 20.9467C7.58593 21.4522 8.26962 21.4619 8.69471 20.9758Z" />
              </svg>
            </button>

            <button
              onClick={goToNext}
              className="absolute right-4 lg:right-8 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-primary-main/40 backdrop-blur-sm flex items-center justify-center text-white hover:bg-primary-main/60 transition-colors shadow-lg"
              aria-label="Próximo"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M15.3053 3.02416C14.8802 3.51023 14.8802 4.31712 15.297 4.81291L20.3654 10.7333H1.07537C0.483501 10.7333 0 11.2972 0 11.9971C0 12.6971 0.483501 13.2609 1.07537 13.2609H20.3571L15.2886 19.1813C14.8718 19.6771 14.8802 20.4743 15.297 20.9701C15.7221 21.4561 16.3974 21.4561 16.8225 20.9603L23.6916 12.8915C23.7833 12.7748 23.8583 12.6485 23.9166 12.4929C23.975 12.3374 24 12.1721 24 12.0068C24 11.6763 23.8916 11.3652 23.6916 11.1222L16.8225 3.05332C16.414 2.5478 15.7305 2.53808 15.3053 3.02416Z" />
              </svg>
            </button>
          </div>

          {/* Dots/Bullets */}
          <div className="flex items-center justify-center gap-2.5 mt-10 lg:mt-16">
            {depoimentos.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`rounded-full transition-all ${
                  index === currentIndex
                    ? "bg-blue-main w-10 h-2.5"
                    : "bg-gray-300 w-2.5 h-2.5 hover:bg-gray-400"
                }`}
                aria-label={`Ir para depoimento ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
